import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { errorMessage, isVersionConflict } from "../lib/api";
import { formatDateTime } from "../lib/missions";
import {
  ATTENDANCE, ATTENDANCE_LABELS, ATTENDED, RESULTS, SESSION_MODES, assessedCount, closingSummary, objectivesToAssess, outcomeSummary,
  sessionWhen, trainingError,
} from "../lib/training";
import { useBottomBar } from "../hooks/useCopilotDock";
import { SessionStatus } from "../components/training/SessionsPanel";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, ChevronUp, Loader2, MoreHorizontal, Plus, Search, Target, Trash2, XCircle,
} from "lucide-react";

const HISTORY_LABELS = {
  created: "Session préparée", updated: "Session modifiée", run_updated: "Déroulé enregistré", participant_added: "Participant ajouté",
  participant_updated: "Présence ou résultats enregistrés", participant_removed: "Participant retiré", closed: "Session clôturée",
  reopened: "Session rouverte", cancelled: "Session annulée",
};

// One training session, phone first: presence in one tap, results objective by objective, then « Clôturer ». Every write goes
// through one queue with the session's latest version, so quick taps never overtake each other; rights come from `capabilities`.
export default function TrainingSession() {
  const { id } = useParams();
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ready | missing | error
  const [errorText, setErrorText] = useState("");
  const [dialog, setDialog] = useState(null); // add | close | cancel | reopen | edit
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(0);
  const latest = useRef(null);
  const queue = useRef(Promise.resolve());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/training/sessions/${id}`);
      latest.current = data;
      setSession(data);
      setPhase("ready");
    } catch (e) {
      if (e?.response?.status === 404) setPhase("missing");
      else { setErrorText(trainingError(e, "Impossible de charger la session.")); setPhase("error"); }
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // `request(current)` receives the latest session (its version, a participant's version) when its turn comes.
  const write = useCallback((request, success) => {
    setSaving((n) => n + 1);
    const run = queue.current.then(async () => {
      try {
        const { data } = await request(latest.current);
        latest.current = data;
        setSession(data);
        if (success) toast.success(success);
        return data;
      } catch (e) {
        toast.error(trainingError(e, "Impossible d'enregistrer."));
        if (isVersionConflict(e)) await load();
        return null;
      } finally {
        setSaving((n) => n - 1);
      }
    });
    queue.current = run.catch(() => null);
    return run;
  }, [load]);

  const caps = session?.capabilities || {};
  useBottomBar(!!caps.close, { raise: true });

  // `resultsPatch` is merged into the participant's results AS THE SERVER LAST SENT THEM, when the request's turn comes: two quick taps
  // on two objectives never send a stale set that would erase the first one.
  const updateParticipant = (participantId, { resultsPatch, ...body }) => write((s) => {
    const p = s.participants.find((x) => x.id === participantId);
    const results = resultsPatch ? { results: { ...(p.results || {}), ...resultsPatch } } : {};
    return api.patch(`/training/sessions/${id}/participants/${participantId}`, { expected_version: p.version, ...body, ...results });
  });
  const updateRun = (body) => write((s) => api.patch(`/training/sessions/${id}/run`, { expected_version: s.version, ...body }));

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-64 w-full" /></div>;
  if (phase === "missing" || phase === "error") {
    return (
      <Card className="p-8 text-center space-y-3 max-w-xl mx-auto" data-testid="training-session-missing">
        <AlertTriangle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-700">{phase === "missing" ? "Cette session n'existe pas ou vous n'y avez pas accès." : errorText}</p>
        <Button variant="outline" asChild><Link to="/formations">Retour aux formations</Link></Button>
      </Card>
    );
  }

  const covered = session.covered_module_ids || [];
  const back = session.mission ? { to: `/missions/${session.mission.id}`, label: "Retour à la mission" } : { to: "/formations", label: "Retour aux formations" };
  const manageMenu = caps.manage || caps.cancel || caps.reopen || caps.delete;
  const confirmDelete = async (reason) => {
    try {
      await api.delete(`/training/sessions/${id}`, { data: { reason } });
      toast.success("Session mise à la corbeille");
      navigate(back.to);
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de supprimer la session."));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className={`space-y-4 ${caps.close ? "pb-28" : ""}`} data-testid="training-session-page">
      <Link to={back.to} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> {back.label}</Link>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="training-session-title">{session.program.label}</h1>
            <SessionStatus status={session.status} />
          </div>
          <p className="text-sm text-slate-600" data-testid="training-session-meta">
            {[sessionWhen(session), SESSION_MODES[session.mode], session.pharmacy?.name].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-slate-500">
            {session.reference} · version {session.program.version_number} du programme
            {session.mission && <> · mission <Link to={`/missions/${session.mission.id}`} className="text-jamora hover:underline">{session.mission.reference}</Link></>}
            {" · "}Formateur{session.trainers.length > 1 ? "s" : ""} : {session.trainers.map((t) => t.name).join(", ")}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {saving > 0 && <Loader2 size={16} className="animate-spin text-slate-400" aria-label="Enregistrement" />}
          {manageMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label="Autres actions" data-testid="session-more"><MoreHorizontal size={16} /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" title="Session">
                {caps.manage && <DropdownMenuItem onClick={() => setDialog("edit")} data-testid="session-edit">Date, formateurs et notes</DropdownMenuItem>}
                {caps.reopen && <DropdownMenuItem onClick={() => setDialog("reopen")} data-testid="session-reopen">Rouvrir la session</DropdownMenuItem>}
                {caps.cancel && <DropdownMenuItem onClick={() => setDialog("cancel")} className="text-red-700" data-testid="session-cancel">Annuler la session</DropdownMenuItem>}
                {caps.delete && <DropdownMenuItem onClick={() => setDeleting(session)} className="text-red-700" data-testid="session-delete">Supprimer…</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {session.status === "closed" && (
        <Card className="p-3 border-emerald-200 bg-emerald-50 text-sm text-emerald-900 flex items-center gap-2" data-testid="session-closed-banner">
          <CheckCircle2 size={16} className="shrink-0" />Clôturée{session.closed_by_name ? ` par ${session.closed_by_name}` : ""}.
          {caps.reopen && " Pour corriger, rouvrez-la avec un motif."}
        </Card>
      )}
      {session.status === "cancelled" && (
        <Card className="p-3 border-slate-200 bg-slate-50 text-sm text-slate-700 flex items-center gap-2">
          <XCircle size={16} className="shrink-0" />Annulée : {session.cancel_reason}
        </Card>
      )}
      {session.notes && <Card className="p-3 text-sm text-slate-700 whitespace-pre-line"><span className="font-medium">Préparation : </span>{session.notes}</Card>}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] items-start">
        <div className="space-y-3 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-slate-900">Participants ({session.participants.length})</h2>
            {(caps.add_collaborator || caps.add_staff) && (
              <Button variant="outline" size="sm" onClick={() => setDialog("add")} data-testid="session-add-participant"><Plus size={14} className="mr-1.5" />Ajouter</Button>
            )}
          </div>
          {session.participants.length === 0 && (
            <Card className="p-6 text-center text-sm text-slate-500" data-testid="session-no-participant">
              Aucun participant pour le moment.{(caps.add_collaborator || caps.add_staff) && " Ajoutez les personnes présentes le jour de la formation."}
            </Card>
          )}
          {session.participants.map((p, index) => (
            <ParticipantCard key={p.id} index={index} participant={p} session={session} covered={covered} canRun={caps.run}
              suggestions={session.suggestions?.[p.id]} onUpdate={(body) => updateParticipant(p.id, body)}
              onRemove={() => write(() => api.delete(`/training/sessions/${id}/participants/${p.id}`), "Participant retiré")} />
          ))}
        </div>
        <div className="space-y-4 lg:sticky lg:top-24">
          <RunCard session={session} canRun={caps.run} onUpdate={updateRun} />
          {session.history?.length > 0 && (
            <details className="rounded-lg border border-slate-200 bg-white p-4" data-testid="session-history">
              <summary className="cursor-pointer text-sm font-medium text-slate-900">Historique</summary>
              <ol className="mt-3 space-y-2">
                {session.history.map((h, i) => (
                  <li key={i} className="text-sm">
                    <span className="text-slate-800">{HISTORY_LABELS[h.kind] || h.kind}</span>
                    <span className="text-xs text-slate-500"> · {formatDateTime(h.at)} · {h.actor_name}</span>
                    {h.reason && <span className="block text-xs text-slate-500">Motif : {h.reason}</span>}
                  </li>
                ))}
              </ol>
            </details>
          )}
        </div>
      </div>

      {caps.close && <CloseBar session={session} onClose={() => setDialog("close")} busy={saving > 0} />}

      <AddParticipantDialog open={dialog === "add"} session={session} onClose={() => setDialog(null)}
        onAdd={(body) => write(() => api.post(`/training/sessions/${id}/participants`, body), "Participant ajouté").then((data) => data && setDialog(null))} />
      <CloseDialog open={dialog === "close"} session={session} onClose={() => setDialog(null)}
        onConfirm={() => write((s) => api.post(`/training/sessions/${id}/close`, { expected_version: s.version }), "Session clôturée").then(() => setDialog(null))} />
      <ReasonDialog open={dialog === "cancel" || dialog === "reopen"} mode={dialog} onClose={() => setDialog(null)}
        onConfirm={(reason) => write((s) => api.post(`/training/sessions/${id}/${dialog}`, { expected_version: s.version, reason }),
          dialog === "cancel" ? "Session annulée" : "Session rouverte").then((data) => data && setDialog(null))} />
      <DeleteWithReasonDialog
        target={deleting} testId="delete-session" onClose={() => setDeleting(null)} onConfirm={confirmDelete}
        title={`Supprimer la session ${deleting?.reference || ""} ?`}
        description="Elle n'a pas démarré et n'a aucun participant. Elle va dans la corbeille : vous pourrez la restaurer pendant 30 jours."
      />
      <EditDialog open={dialog === "edit"} session={session} onClose={() => setDialog(null)}
        onDone={(data) => { latest.current = data; setSession(data); setDialog(null); }} />
    </div>
  );
}

function Chip({ active, onClick, disabled, children, className = "", testid }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={active} data-testid={testid}
      className={`min-h-[36px] rounded-full border px-3 py-1 text-sm font-medium transition-colors disabled:opacity-60 ${active ? className || "bg-jamora text-white border-jamora" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

function CommentField({ value, disabled, onSave, testid }) {
  const [draft, setDraft] = useState(value || "");
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(value || ""); }, [value]);
  return (
    <Textarea rows={2} maxLength={2000} className="text-base md:text-sm" placeholder="Commentaire (facultatif)" value={draft} disabled={disabled}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; if ((draft.trim() || null) !== (value || null)) onSave(draft.trim() || null); }}
      onChange={(e) => setDraft(e.target.value)} aria-label="Commentaire" data-testid={testid} />
  );
}

function ParticipantCard({ index, participant: p, session, covered, canRun, suggestions, onUpdate, onRemove }) {
  const attended = ATTENDED.includes(p.attendance);
  const [open, setOpen] = useState(false);
  const groups = objectivesToAssess(session.content, covered);
  const count = assessedCount(p, session.content, covered);
  const setResult = (objectiveId, value) => onUpdate({ resultsPatch: { [objectiveId]: value } });
  const closed = session.status === "closed";

  return (
    <Card className="p-3 sm:p-4 space-y-3" data-testid={`participant-${index}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-slate-900 break-words" data-testid={`participant-name-${index}`}>{p.name}</p>
          <p className="text-xs text-slate-500">
            {p.kind === "collaborator" ? "Collaborateur Jamora" : p.masked ? "Nom visible par l'équipe et les organisateurs de la mission" : p.jamora_role_label || "Personnel de la pharmacie"}
            {!canRun && ` · ${ATTENDANCE_LABELS[p.attendance]}`}
            {p.performed_by && ` · évalué par ${p.performed_by.name}`}
          </p>
        </div>
        {canRun && p.attendance === "invited" && !Object.keys(p.results || {}).length && (
          <Button variant="ghost" size="icon" aria-label={`Retirer ${p.name}`} className="text-slate-400 hover:text-red-700 shrink-0" onClick={onRemove}
            data-testid={`participant-remove-${index}`}><Trash2 size={14} /></Button>
        )}
      </div>

      {canRun && (
        <div className="flex flex-wrap gap-2" role="group" aria-label={`Présence de ${p.name}`}>
          {ATTENDANCE.map((a) => (
            <Chip key={a.value} active={p.attendance === a.value} onClick={() => p.attendance !== a.value && onUpdate({ attendance: a.value })}
              testid={`participant-${index}-attendance-${a.value}`}>{a.label}</Chip>
          ))}
        </div>
      )}

      {closed && attended && p.outcomes?.length > 0 && (
        <div className="space-y-1" data-testid={`participant-${index}-outcomes`}>
          <p className="text-sm font-medium text-slate-800">{outcomeSummary(p.outcomes)}</p>
          <ul className="space-y-0.5">
            {p.outcomes.map((o) => (
              <li key={o.module_id} className="flex items-center gap-1.5 text-sm text-slate-700">
                {o.passed ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> : <XCircle size={14} className="text-slate-400 shrink-0" />}
                {o.title} <span className="text-xs text-slate-500">· {o.acquired}/{o.total} objectif{o.total > 1 ? "s" : ""} acquis</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {suggestions?.length > 0 && p.collaborator_id && (
        <p className="text-xs text-slate-600 rounded-md bg-slate-50 p-2" data-testid={`participant-${index}-suggestions`}>
          Compétences que ces résultats peuvent appuyer : {suggestions.map((s) => s.label).join(", ")}.{" "}
          <Link to={`/collaborateurs/${p.collaborator_id}`} className="text-jamora underline">Ouvrir sa fiche</Link> pour fixer un niveau ou accorder une habilitation.
        </p>
      )}

      {attended && (canRun || Object.keys(p.results || {}).length > 0) && !closed && (
        <div className="space-y-2">
          <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-sm font-medium text-jamora"
            aria-expanded={open} data-testid={`participant-${index}-toggle-results`}>
            Résultats · {count.done}/{count.total} objectif{count.total > 1 ? "s" : ""} noté{count.done > 1 ? "s" : ""}
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {open && (
            <div className="space-y-3">
              {groups.map(({ module, objectives }) => (
                <div key={module.module_id} className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{module.title}</p>
                  {objectives.map((o) => (
                    <div key={o.id} className="space-y-1.5" data-testid={`participant-${index}-objective-${o.id}`}>
                      <p className="flex items-start gap-1.5 text-sm text-slate-800"><Target size={13} className="mt-0.5 shrink-0 text-slate-400" />{o.text}</p>
                      <div className="flex flex-wrap gap-1.5 pl-5">
                        {RESULTS.map((r) => (
                          <Chip key={r.value} active={p.results?.[o.id] === r.value} className={r.className} disabled={!canRun}
                            onClick={() => p.results?.[o.id] !== r.value && setResult(o.id, r.value)} testid={`participant-${index}-${o.id}-${r.value}`}>
                            {r.short}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {(canRun || p.comment) && (canRun ? <CommentField value={p.comment} disabled={!canRun} onSave={(comment) => onUpdate({ comment })} testid={`participant-${index}-comment`} />
        : <p className="text-sm text-slate-600 whitespace-pre-line">{p.comment}</p>)}
    </Card>
  );
}

function TextBlur({ id, label, value, disabled, onSave, testid }) {
  const [draft, setDraft] = useState(value || "");
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(value || ""); }, [value]);
  if (disabled) {
    return value ? <div className="space-y-0.5"><p className="text-xs font-medium text-slate-500">{label}</p><p className="text-sm text-slate-700 whitespace-pre-line">{value}</p></div> : null;
  }
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Textarea id={id} rows={3} maxLength={4000} className="text-base md:text-sm" value={draft} data-testid={testid}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; if ((draft.trim() || null) !== (value || null)) onSave(draft.trim() || null); }}
        onChange={(e) => setDraft(e.target.value)} />
    </div>
  );
}

function RunCard({ session, canRun, onUpdate }) {
  const covered = session.covered_module_ids || [];
  const toggle = (moduleId) => onUpdate({ covered_module_ids: covered.includes(moduleId) ? covered.filter((m) => m !== moduleId) : [...covered, moduleId] });
  return (
    <Card className="p-4 space-y-4" data-testid="session-run">
      <div className="space-y-2">
        <h2 className="text-base font-semibold text-slate-900">Modules traités</h2>
        <ul className="space-y-1.5">
          {session.content.map((item) => (
            <li key={item.module_id}>
              <label className={`flex items-start gap-2.5 text-sm ${canRun ? "cursor-pointer" : ""}`} data-testid={`session-module-${item.module_id}`}>
                <Checkbox checked={covered.includes(item.module_id)} disabled={!canRun} onCheckedChange={() => toggle(item.module_id)} className="mt-0.5" />
                <span>
                  <span className="text-slate-800">{item.title}</span>
                  <span className="block text-xs text-slate-500">
                    {item.required ? "Obligatoire" : "Facultatif"} · {(item.objectives || []).length} objectif{(item.objectives || []).length > 1 ? "s" : ""}
                    {item.pass_threshold ? ` · réussite à ${item.pass_threshold} %` : ""}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
      <TextBlur id="session-obstacles" label="Obstacles rencontrés" value={session.obstacles} disabled={!canRun} onSave={(v) => onUpdate({ obstacles: v })}
        testid="session-obstacles" />
      <TextBlur id="session-recommendations" label="Recommandations" value={session.recommendations} disabled={!canRun}
        onSave={(v) => onUpdate({ recommendations: v })} testid="session-recommendations" />
    </Card>
  );
}

function CloseBar({ session, onClose, busy }) {
  const { problems } = closingSummary(session);
  return (
    <div className="fixed inset-x-0 bottom-[4.75rem] lg:bottom-4 z-30 px-4 lg:left-64" data-testid="session-close-bar">
      <div className="mx-auto max-w-4xl rounded-xl border border-slate-200 bg-white p-3 shadow-lg flex items-center justify-between gap-3">
        <span className="text-xs text-slate-600 min-w-0">{problems.length ? <span className="text-amber-800">{problems[0].msg}</span> : "Prête à être clôturée."}</span>
        <Button size="sm" onClick={onClose} disabled={busy || problems.length > 0} className="shrink-0" data-testid="session-close">Clôturer</Button>
      </div>
    </div>
  );
}

function CloseDialog({ open, session, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const { unassessed } = closingSummary(session);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="session-close-dialog">
        <DialogHeader>
          <DialogTitle>Clôturer la session ?</DialogTitle>
          <DialogDescription>
            Le CRM calcule les modules réussis de chaque participant avec le seuil de chaque module. Ensuite, plus rien ne change sauf si un organisateur la rouvre.
          </DialogDescription>
        </DialogHeader>
        {unassessed > 0 && (
          <p className="text-sm text-amber-800" role="alert">
            {unassessed} participant{unassessed > 1 ? "s ont" : " a"} encore des objectifs sans résultat : ils seront enregistrés « non évalué ».
          </p>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }} disabled={busy} data-testid="session-close-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Clôturer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReasonDialog({ open, mode, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const cancel = mode === "cancel";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="session-reason-dialog">
        <DialogHeader>
          <DialogTitle>{cancel ? "Annuler la session ?" : "Rouvrir la session ?"}</DialogTitle>
          <DialogDescription>{cancel ? "Elle reste dans l'historique avec son motif." : "Présence et résultats redeviennent modifiables ; le motif est gardé dans l'historique."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="session-reason">Motif *</Label>
          <Textarea id="session-reason" rows={2} maxLength={300} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="session-reason" />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Retour</Button>
          <Button onClick={async () => { setBusy(true); await onConfirm(reason.trim()); setBusy(false); }} disabled={busy || reason.trim().length < 3}
            className={cancel ? "bg-red-600 hover:bg-red-700 text-white" : ""} data-testid="session-reason-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{cancel ? "Annuler la session" : "Rouvrir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Date, time, notes and trainers. Trainers: the mission's team, or (without mission) accounts and fiches found by name. A trainer without
// the expected accreditation is a warning to confirm (decision 4), as when the session was prepared.
function EditDialog({ open, session, onClose, onDone }) {
  const [form, setForm] = useState({ date: "", start_time: "", notes: "", trainers: [] });
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState("");
  const [warnings, setWarnings] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const missionId = session.mission?.id;
  useEffect(() => {
    if (!open) return;
    setForm({ date: session.date, start_time: session.start_time || "", notes: session.notes || "", trainers: session.trainers });
    setSearch(""); setWarnings(null); setConfirmed(false);
  }, [open, session]);
  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/training/sessions/trainer-candidates", { params: missionId ? { mission_id: missionId } : { search } });
        setPeople(data.people);
      } catch (e) {
        setPeople([]);
      }
    }, missionId ? 0 : 250);
    return () => clearTimeout(handle);
  }, [open, missionId, search]);

  const change = (patch) => { setForm((f) => ({ ...f, ...patch })); setWarnings(null); setConfirmed(false); };
  const same = (a, b) => a.kind === b.kind && a.id === b.id;
  const toggle = (person) => change({ trainers: form.trainers.some((t) => same(t, person)) ? form.trainers.filter((t) => !same(t, person)) : [...form.trainers, person] });
  const shown = [...form.trainers.filter((t) => !people.some((p) => same(p, t))), ...people];

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/training/sessions/${session.id}`, {
        expected_version: session.version, date: form.date, start_time: form.start_time || null, notes: form.notes.trim() || null,
        trainers: form.trainers.map((t) => ({ kind: t.kind, id: t.id })),
        confirm_warnings: confirmed && warnings ? [...new Set(warnings.map((w) => w.kind))] : [],
      });
      toast.success("Session modifiée");
      onDone(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "warnings") setWarnings(detail.warnings);
      else toast.error(trainingError(e, "Impossible de modifier la session."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="session-edit-dialog">
        <DialogHeader><DialogTitle>Modifier la session</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label htmlFor="edit-date">Date</Label>
              <Input id="edit-date" type="date" className="text-base md:text-sm" value={form.date} onChange={(e) => change({ date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label htmlFor="edit-time">Heure</Label>
              <Input id="edit-time" type="time" className="text-base md:text-sm" value={form.start_time} onChange={(e) => change({ start_time: e.target.value })} /></div>
          </div>
          <div className="space-y-1.5">
            <Label>Formateurs</Label>
            <p className="text-xs text-slate-500">{missionId ? "Parmi l'équipe de la mission." : "Un compte du CRM ou une fiche de l'annuaire."}</p>
            {!missionId && (
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une personne" className="pl-8 text-base md:text-sm"
                  aria-label="Rechercher un formateur" />
              </div>
            )}
            <div className="max-h-48 overflow-y-auto scrollbar-thin space-y-1" data-testid="session-edit-trainers">
              {shown.map((p) => (
                <label key={`${p.kind}-${p.id}`} className="flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-slate-50 cursor-pointer"
                  data-testid={`session-edit-trainer-${p.id}`}>
                  <Checkbox checked={form.trainers.some((t) => same(t, p))} onCheckedChange={() => toggle(p)} />
                  <span className="text-sm text-slate-800">{p.name}</span>
                  {p.kind === "collaborator" && <span className="text-xs text-slate-500">sans compte CRM</span>}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="edit-notes">Notes de préparation</Label>
            <Textarea id="edit-notes" rows={3} maxLength={4000} className="text-base md:text-sm" value={form.notes} onChange={(e) => change({ notes: e.target.value })} /></div>
          {warnings && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" role="alert" data-testid="session-edit-warnings">
              {warnings.map((w, i) => (
                <p key={i} className="text-sm text-amber-900">{w.trainer.name} n'a pas d'habilitation valide ce jour-là pour : {w.skills.join(", ")}.</p>
              ))}
              <label className="flex items-center gap-2 text-sm text-amber-900 cursor-pointer">
                <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="session-edit-warnings-confirm" />
                Je confirme : ce formateur anime quand même la session.
              </label>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button disabled={busy || !form.date || !form.trainers.length || (warnings && !confirmed)} data-testid="session-edit-confirm" onClick={save}>
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddParticipantDialog({ open, session, onClose, onAdd }) {
  const caps = session.capabilities;
  const [kind, setKind] = useState(caps.add_staff ? "pharmacy_staff" : "collaborator");
  const [staff, setStaff] = useState({ first_name: "", last_name: "", jamora_role: "" });
  const [search, setSearch] = useState("");
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setKind(caps.add_staff ? "pharmacy_staff" : "collaborator"); setStaff({ first_name: "", last_name: "", jamora_role: "" }); setSearch(""); }
  }, [open, caps.add_staff]);
  useEffect(() => {
    if (!open || kind !== "collaborator") return undefined;
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get(`/training/sessions/${session.id}/participant-candidates`, { params: { search } });
        setPeople(data.people);
      } catch (e) {
        setPeople([]);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [open, kind, search, session.id]);
  const add = async (body) => { setBusy(true); await onAdd(body); setBusy(false); };
  const staffReady = staff.first_name.trim() && staff.last_name.trim() && staff.jamora_role;
  const kinds = useMemo(() => [caps.add_staff && ["pharmacy_staff", "Personnel de la pharmacie"], caps.add_collaborator && ["collaborator", "Collaborateur Jamora"]].filter(Boolean),
    [caps.add_staff, caps.add_collaborator]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="session-add-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter un participant</DialogTitle>
          <DialogDescription>Pour le personnel d'une pharmacie : prénom, nom et rôle Jamora seulement, jamais de coordonnées.</DialogDescription>
        </DialogHeader>
        {kinds.length > 1 && (
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 self-start" role="tablist">
            {kinds.map(([value, label]) => (
              <button key={value} type="button" role="tab" aria-selected={kind === value} onClick={() => setKind(value)} data-testid={`session-add-kind-${value}`}
                className={`rounded-md px-3 py-1.5 text-sm ${kind === value ? "bg-jamora text-white" : "text-slate-600"}`}>{label}</button>
            ))}
          </div>
        )}
        {kind === "pharmacy_staff" ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label htmlFor="staff-first">Prénom *</Label>
                <Input id="staff-first" maxLength={80} className="text-base md:text-sm" value={staff.first_name} onChange={(e) => setStaff({ ...staff, first_name: e.target.value })}
                  data-testid="staff-first-name" /></div>
              <div className="space-y-1.5"><Label htmlFor="staff-last">Nom *</Label>
                <Input id="staff-last" maxLength={80} className="text-base md:text-sm" value={staff.last_name} onChange={(e) => setStaff({ ...staff, last_name: e.target.value })}
                  data-testid="staff-last-name" /></div>
            </div>
            <div className="space-y-1.5"><Label htmlFor="staff-role">Rôle dans Jamora *</Label>
              <Select value={staff.jamora_role} onValueChange={(value) => setStaff({ ...staff, jamora_role: value })}>
                <SelectTrigger id="staff-role" data-testid="staff-role"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{session.roles.map((r) => <SelectItem key={r.code} value={r.code} data-testid={`staff-role-${r.code}`}>{r.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher dans l'annuaire" className="pl-8 text-base md:text-sm"
                aria-label="Rechercher un collaborateur" data-testid="session-add-search" />
            </div>
            <div className="max-h-64 overflow-y-auto scrollbar-thin space-y-1">
              {people.length === 0 && <p className="text-sm text-slate-500 py-3 text-center">Aucun collaborateur trouvé.</p>}
              {people.map((c) => (
                <button key={c.id} type="button" disabled={busy} onClick={() => add({ kind: "collaborator", collaborator_id: c.id })}
                  className="w-full text-left rounded-md px-2 py-2 hover:bg-slate-50" data-testid={`session-add-collaborator-${c.id}`}>
                  <span className="block text-sm text-slate-800">{c.name}</span>
                  {c.job_title && <span className="block text-xs text-slate-500">{c.job_title}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Fermer</Button>
          {kind === "pharmacy_staff" && (
            <Button disabled={busy || !staffReady} data-testid="session-add-confirm"
              onClick={() => add({ kind: "pharmacy_staff", first_name: staff.first_name.trim(), last_name: staff.last_name.trim(), jamora_role: staff.jamora_role })}>
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Ajouter
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
